import { createContext, useContext, useState, ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { launchConfigService } from '@/services/launch-config.service';
import { toast } from 'react-hot-toast';

interface LoansRefreshContextType {
  refreshing: boolean;
  longRefresh?: boolean;
  handleRefresh: () => void;
}

const LoansRefreshContext = createContext<LoansRefreshContextType | undefined>(
  undefined
);

export const LoansRefreshProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [longRefresh, setLongRefresh] = useState(false);

  const refreshViewsMutation = useMutation({
    mutationFn: () => launchConfigService.refreshMaterializedViews(),
    retry: 0,
    onSuccess: async (result) => {
      toast.success(`Refresh completed in ${result.duration_seconds}s`);
      setLongRefresh(false);
      await queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
    onError: (error) => {
      console.error('Error refreshing materialized views:', error);

      const axiosError = error as any;
      const status = axiosError?.response?.status;
      const code = axiosError?.code;
      const url = axiosError?.config?.url;

      if (status === 504) {
        setLongRefresh(true);
        return;
      }

      if (code === 'ECONNABORTED' && url === '/api/v1/valuation/refresh-materialized-views') {
        setLongRefresh(true);
        return;
      }

      toast.error('Failed to refresh materialized views');
    },
    onSettled: () => {
      setRefreshing(false);
    },
  });

  const handleRefresh = () => {
    setLongRefresh(false);
    setRefreshing(true);
    refreshViewsMutation.mutate();
  };

  return (
    <LoansRefreshContext.Provider
      value={{ refreshing, longRefresh, handleRefresh }}
    >
      {children}
    </LoansRefreshContext.Provider>
  );
};

export const useLoansRefresh = () => {
  const context = useContext(LoansRefreshContext);
  if (context === undefined) {
    throw new Error('useLoansRefresh must be used within LoansRefreshProvider');
  }
  return context;
};

